import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { appelApi, ErreurApi, EVENEMENT_SESSION_EXPIREE } from '../lib/api';
import { CLE_MARQUE } from '../marque/marque';
import { retirerCetAppareil, verifierCetAppareil } from '../lib/usePush';
import type { UtilisateurConnecte } from '../lib/types';
import { ContexteAuthentification, type ContexteAuth } from './context';

export function FournisseurAuth({ children }: { children: ReactNode }): JSX.Element {
  const [utilisateur, setUtilisateur] = useState<UtilisateurConnecte | null>(null);
  const [chargement, setChargement] = useState(true);
  const clientRequetes = useQueryClient();
  const compteCourant = useRef<string | null>(null);

  // Les réponses en cache appartiennent au compte qui les a lues : quand la
  // session se ferme ou change de compte, elles partent avec elle. Sans ça, le
  // compte suivant ouvert dans le même onglet verrait un instant le tableau de
  // bord du précédent avant que le sien n'arrive (TF-450). La marque, publique,
  // reste.
  const changerDeSession = useCallback(
    (suivant: UtilisateurConnecte | null) => {
      const id = suivant?.id ?? null;
      if (compteCourant.current !== null && compteCourant.current !== id) {
        clientRequetes.removeQueries({ predicate: (requete) => requete.queryKey[0] !== CLE_MARQUE[0] });
      }
      // Un appareil laissé abonné par un autre compte cesse de recevoir ses
      // notifications push (TF-460).
      if (id !== null && compteCourant.current !== id) void verifierCetAppareil();
      compteCourant.current = id;
      setUtilisateur(suivant);
    },
    [clientRequetes],
  );

  // Une requête 401 en cours de session (jeton expiré, secret tourné, compte
  // désactivé) vide `utilisateur` : `ZonePrivee` renvoie alors vers
  // `/connexion` au lieu de laisser chaque écran afficher l'erreur brute de
  // sa propre requête.
  useEffect(() => {
    const surSessionExpiree = () => changerDeSession(null);
    window.addEventListener(EVENEMENT_SESSION_EXPIREE, surSessionExpiree);
    return () => window.removeEventListener(EVENEMENT_SESSION_EXPIREE, surSessionExpiree);
  }, [changerDeSession]);

  // Vérification de la session au chargement : le cookie httpOnly n'est pas
  // lisible en JavaScript, seule l'API peut dire si la session est valide.
  useEffect(() => {
    let annule = false;

    appelApi<{ utilisateur: UtilisateurConnecte }>('/auth/me')
      .then((reponse) => {
        if (!annule) changerDeSession(reponse.utilisateur);
      })
      .catch((erreur) => {
        if (!(erreur instanceof ErreurApi) || !erreur.estNonAuthentifie) {
          console.error('[auth] vérification de session impossible', erreur);
        }
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });

    return () => {
      annule = true;
    };
  }, [changerDeSession]);

  const connexion = useCallback(async (email: string, motDePasse: string) => {
    const reponse = await appelApi<{ utilisateur: UtilisateurConnecte }>('/auth/login', {
      methode: 'POST',
      corps: { email, motDePasse },
    });
    changerDeSession(reponse.utilisateur);
  }, [changerDeSession]);

  const installerSession = useCallback(
    (connecte: UtilisateurConnecte) => {
      changerDeSession(connecte);
    },
    [changerDeSession],
  );

  const deconnexion = useCallback(async () => {
    try {
      // Avant la fermeture de la session : le retrait de l'appareil la demande.
      await retirerCetAppareil();
      await appelApi<void>('/auth/logout', { methode: 'POST' });
    } finally {
      changerDeSession(null);
    }
  }, [changerDeSession]);

  const peutAcceder = useCallback(
    (ecran: string) => Boolean(utilisateur?.ecransAutorises.includes(ecran)),
    [utilisateur],
  );

  const valeur = useMemo<ContexteAuth>(
    () => ({ utilisateur, chargement, connexion, installerSession, deconnexion, peutAcceder }),
    [utilisateur, chargement, connexion, installerSession, deconnexion, peutAcceder],
  );

  return (
    <ContexteAuthentification.Provider value={valeur}>
      {children}
    </ContexteAuthentification.Provider>
  );
}

export { useAuth } from './useAuth';
